import { useEffect, useRef } from 'react';

// Drag-to-pan. Press in the timeline's free space and shove the chart about,
// both axes, like dragging a map.
//
// The container is overflow-x-hidden - the horizontal scrollbar duplicated the
// footer date slider and the two fought (index.css:389) - so there is nothing
// to grab. Hiding the scrollbar removed the HANDLE, not the scrolling: direct
// scrollLeft assignment still moves a hidden axis, which is how the footer
// slider, the nav buttons, Today and Follow mode all already work. A pan is
// that same write with the pointer supplying the delta.
//
// NOT scrollTo. It moves this container nowhere (2026-08-23, cost a day).
//
// ⛔ POINTER EVENTS SINCE T368 (2026-08-30), so the chart pans by finger as well
// as by mouse - and THE OLD `e.buttons` GUARD IS GONE WITH THE CONVERSION.
//
// That guard existed because a mouseup could go missing (released outside the
// window, or swallowed) and leave the pan armed, with the chart then following
// an unpressed cursor until the next click. The comment beside it said outright
// that "Pointer Events + setPointerCapture would handle this natively, but every
// existing drag in these two charts is built on mouse events; one odd one out is
// worse than one guard." That reasoning INVERTS once they are all converted:
// capture delivers pointerup to this element even when the release happens
// outside the window, and pointercancel covers a gesture the system steals.
//
// Two things make that safe, and neither is optional - see setPointerCapture's
// guard and the lostpointercapture listener below.
//
// FREE SPACE means the press landed on the row's timeline background ITSELF.
// Everything with its own pointer behaviour - a bar, a resize handle, a
// dependency handle - is a CHILD of that background, so it is a different
// event target and fails the test. That is deliberately a property of the DOM
// rather than a list of exceptions: a new interactive element added inside a
// bar is excluded without anyone remembering to come back here. The grid, the
// today marker and the project row's weekend overlay are pointer-events-none,
// so a press falls THROUGH them onto the background - which is what makes the
// single-target test enough.
//
// The dependency arrows are NOT among those, however they look. The SVG root
// is pointer-events-none but every arrow group puts it back
// (DependencyArrows.js:643) because arrows are clickable - delete, recolour,
// chain hover. So a press on an arrow line does not fall through; it hits the
// arrow, is not the background, and starts no pan. Correct, and by the same
// rule as a bar rather than by an exception.
//
// `disabled` has NO default and the options object is NOT optional (Bill,
// 2026-08-24, on CLAUDE.md's no-fallback rule). Both charts state what they
// want out loud - `followMode` here, `false` on templates - so a call that
// forgot it would fail visibly rather than quietly panning through a state
// that was meant to lock the chart. `pointersRef` is required on the same
// terms: it is the map useGanttGestures writes, and the pan reads it to answer
// "is another finger already down".
const useGanttPan = (scrollContainerRef, { disabled, pointersRef }) => {
    const panRef = useRef(null);

    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el || disabled) return undefined;

        // The chart moves OPPOSITE the pointer: drag left and later dates come
        // in from the right.
        //
        // Clamped HERE rather than left to the browser, and the anchor re-based
        // when it clamps. Assigning past the end is clamped either way, but the
        // anchor would keep counting - drag 500px beyond the left edge and the
        // first 500px of the way back would move nothing. Re-basing at the edge
        // removes that dead zone.
        const onPointerMove = (e) => {
            const pan = panRef.current;
            if (!pan) return;
            if (e.pointerId !== pan.pointerId) return;

            const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
            const wantLeft = pan.scrollLeft - (e.clientX - pan.startX);
            const nextLeft = Math.min(maxLeft, Math.max(0, wantLeft));
            pan.scrollLeft += nextLeft - wantLeft;
            el.scrollLeft = nextLeft;

            const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
            const wantTop = pan.scrollTop - (e.clientY - pan.startY);
            const nextTop = Math.min(maxTop, Math.max(0, wantTop));
            pan.scrollTop += nextTop - wantTop;
            el.scrollTop = nextTop;
        };

        // ⛔ IDEMPOTENT, BECAUSE BOTH pointerup AND lostpointercapture FIRE ON AN
        // ORDINARY RELEASE. The browser releases capture implicitly at pointerup
        // and then fires lostpointercapture, so this runs TWICE every time. The
        // ref is nulled first, so the second call returns here.
        const onPointerUp = () => {
            if (!panRef.current) return;
            panRef.current = null;
            document.body.classList.remove('gantt-panning');
        };

        const onPointerDown = (e) => {
            // Left button only, and the PRIMARY pointer only. Middle-click is
            // the browser's own autoscroll and right-click is the context menu;
            // neither is ours to take, and a second finger must not start a
            // second pan on top of the first.
            if (e.button !== 0 || !e.isPrimary) return;
            if (!e.target || !e.target.dataset) return;
            if (e.target.dataset.ganttPan === undefined) return;

            // ⛔ EXCLUDE OUR OWN pointerId, NEVER `size >= 1`.
            //
            // useGanttGestures listens on this SAME element and has already
            // recorded this very pointer by the time we run - or has not,
            // depending on hook call order, which nothing pins. Asking whether
            // any OTHER pointer is down is true regardless of who ran first; a
            // naive size check refuses every one-finger pan on a coin flip.
            const others = [...pointersRef.current.keys()].filter(id => id !== e.pointerId);
            if (others.length > 0) return;

            panRef.current = {
                pointerId: e.pointerId,
                startX: e.clientX,
                startY: e.clientY,
                scrollLeft: el.scrollLeft,
                scrollTop: el.scrollTop
            };

            // ⛔ setPointerCapture THROWS NotFoundError if the pointer is already
            // inactive - a press whose pointer has gone by the time this runs
            // must not throw out of the press path and leave the chart
            // half-armed.
            try {
                el.setPointerCapture(e.pointerId);
            } catch (err) {
                // The pointer went away between the event and here. Nothing to
                // capture, and the drag will end on its own.
            }

            // preventDefault below stops the drag painting a text selection
            // across every row it crosses - but it also suppresses the FOCUS
            // CHANGE a press normally causes, and the left panel's inline date
            // editor commits on blur (GanttChart.js, TemplateGantt.js). Left
            // alone, a press on free space would leave that input open and still
            // focused right through the pan, the typed date would never land,
            // and keystrokes mid-pan would go on editing it. So blur it by hand
            // first - exactly what the unprevented press would have done, and no
            // more.
            if (document.activeElement && document.activeElement !== document.body) {
                document.activeElement.blur();
            }
            e.preventDefault();
            document.body.classList.add('gantt-panning');
        };

        el.addEventListener('pointerdown', onPointerDown);
        el.addEventListener('pointermove', onPointerMove);
        el.addEventListener('pointerup', onPointerUp);
        el.addEventListener('pointercancel', onPointerUp);
        // ⛔ lostpointercapture IS AN END PATH, not an event to ignore. The
        // browser force-releases capture when the captured element leaves the
        // DOM - which this chart does on a re-render, a scale switch, or
        // entering Follow mode. Without it the stuck pan the old `e.buttons`
        // guard existed for comes back in a RARER form, which is worse because
        // it is harder to reproduce.
        el.addEventListener('lostpointercapture', onPointerUp);

        return () => {
            el.removeEventListener('pointerdown', onPointerDown);
            el.removeEventListener('pointermove', onPointerMove);
            el.removeEventListener('pointerup', onPointerUp);
            el.removeEventListener('pointercancel', onPointerUp);
            el.removeEventListener('lostpointercapture', onPointerUp);
            document.body.classList.remove('gantt-panning');
            panRef.current = null;
        };
    }, [scrollContainerRef, disabled, pointersRef]);

    // The ref itself, for the hover-card guards. A REF and not state on purpose:
    // it is only ever read inside event handlers, at event time, so state would
    // re-render the whole chart twice per drag to tell it something no render
    // uses. `panRef` is already null when idle and an object while a pan is
    // live, so there is no second flag to keep in step with it.
    return { isPanningRef: panRef };
};

export { useGanttPan };
